// deploy: 04-11-2026
import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom/client";
import { AppRoutes } from "./routes";
import reportWebVitals from "./reportWebVitals";
import "@aidron/aidron-ds/lib/bundle.css";
import { SessionWarningModal } from "./components/SharedComponents/sharedComponents.index";
import { i18n } from "./translate/i18n";
import { refreshToken } from "./model/services/AuthService";

const root = ReactDOM.createRoot(document.getElementById("root"));
const { t } = i18n;

function RootApp() {
  const [sessionWarning, setSessionWarning] = useState(false);
  const warningTimerRef = useRef(null);
  const checkIntervalRef = useRef(null);
  const userDismissedWarningRef = useRef(false);

  const handleLogout = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
    localStorage.removeItem("token");
    localStorage.removeItem("exp");
    localStorage.removeItem("store-information");
    localStorage.removeItem("active-store");
    window.location.href = "/";
  }, []);

  const checkSessionExpiration = useCallback(() => {
    const exp = localStorage.getItem("exp");
    
    if (!exp) return;

    const expTime = parseInt(exp, 10) * 1000;
    const now = Date.now();
    const timeLeftForCurrentSession = expTime - now;
    const thresholdTwoMinutesWarning = 2 * 60 * 1000;

    if (timeLeftForCurrentSession <= 0) {
      handleLogout();
      return;
    }

    if (timeLeftForCurrentSession <= thresholdTwoMinutesWarning && !sessionWarning && !userDismissedWarningRef.current) {
      setSessionWarning(true);
    } else if (timeLeftForCurrentSession > thresholdTwoMinutesWarning) {
      userDismissedWarningRef.current = false;
      
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }

      const delay = timeLeftForCurrentSession - thresholdTwoMinutesWarning;
      
      warningTimerRef.current = setTimeout(() => {
        setSessionWarning(true);
      }, delay);
    }
  }, [sessionWarning, handleLogout]);

  const handleContinueSession = async () => {
    try {
      const data = await refreshToken();
      
      localStorage.setItem("token", data.token);
      localStorage.setItem("exp", data.exp);
      
      userDismissedWarningRef.current = true;
      setSessionWarning(false);
      
      checkSessionExpiration();
      
    } catch (error) {
      handleLogout();
    }
  };

  useEffect(() => {
    checkSessionExpiration();

    checkIntervalRef.current = setInterval(() => {
      checkSessionExpiration();
    }, 30000);

    return () => {
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [checkSessionExpiration]);

  return (
    <React.StrictMode>
      <AppRoutes />
      {sessionWarning && (
        <SessionWarningModal
          actions={{
            onClose: () => {
              userDismissedWarningRef.current = true;
              setSessionWarning(false);
            },
            onContinueSession: handleContinueSession,
            onLogout: handleLogout
          }}
          uiState={{ t: t }}
        />
      )}
    </React.StrictMode>
  );
}

root.render(<RootApp />);
reportWebVitals();
